import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Briefcase, Users, Clock, AlertTriangle, Plus, Eye, UserCheck,
  Download, Activity, Loader2, ChevronRight, Calendar, ClipboardList, MapPin
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';
import EstimatePerformanceTile from '../components/EstimatePerformanceTile';
import ProjectProfitabilityTile from '../components/ProjectProfitabilityTile';
import RevenueByProjectTile from '../components/RevenueByProjectTile';
import SatisfactionTile from '../components/SatisfactionTile';
import SafetyAttestationOnboardingBanner from '../components/SafetyAttestationOnboardingBanner';
import OnboardingChecklist from '../components/OnboardingChecklist';
import WelcomeTour from '../components/WelcomeTour';
import { TeamQuickClock } from '../components/TeamQuickClock';
import ClockStatusWidget from '../components/ClockStatusWidget';
import { MissedClockInsCard } from '../components/MissedClockInsCard';
import { RateGoalCard } from '../components/RateGoalCard';
import { JobsHousekeepingCard } from '../components/JobsHousekeepingCard';

const API = `${window.__BACKEND_URL__}/api`;

const STATUS_LABELS = {
  pending: 'Pending', todo: 'To Do', accepted: 'Accepted',
  in_progress: 'In Progress', paused: 'Paused',
  completed: 'Completed', rejected: 'Rejected'
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, company, token, loading, isAdmin, isSupervisor } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [exporting, setExporting] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);
  const [extendedJobs, setExtendedJobs] = useState([]);
  const [timelineJob, setTimelineJob] = useState(null);

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchLiveStatus();
      fetchExtendedJobs();
    }
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLiveStatus = async () => {
    try {
      const res = await axios.get(`${API}/jobs/live-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveStatus(res.data);
    } catch (err) {
      console.error('live-status fetch error:', err);
    }
  };

  const fetchExtendedJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs/extended`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExtendedJobs(res.data?.jobs || []);
    } catch (err) {
      console.error('extended-jobs fetch error:', err);
    }
  };

  const handleExportCSV = async () => {
    if (!exportStart || !exportEnd) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API}/export/payroll-csv`, {
        params: { start_date: exportStart, end_date: exportEnd },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${exportStart}_to_${exportEnd}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { console.error('Export error:', err); }
    finally { setExporting(false); }
  };

  if (loading || isLoading) return <DashSkeleton />;
  if (!token || !user) { navigate('/signin'); return null; }
  if (!isAdmin && !isSupervisor) { navigate('/home'); return null; }

  const s = data?.summary || {};
  const charts = data?.charts || {};
  const activeTotal = (s.jobs_pending || 0) + (s.jobs_in_progress || 0) + (s.jobs_paused || 0);

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="dashboard-page">
      {/* Admin-only one-time nudge: review the auto-generated OSHA / OH&S
          safety attestation. Dismisses itself once reviewed. */}
      <SafetyAttestationOnboardingBanner isAdmin={isAdmin} />

      {/* First-run "getting started" checklist — auto-hides once the core
          steps (staff, team, first job) are done, or when dismissed. */}
      <OnboardingChecklist />

      {/* Three-step spotlight tour for brand-new admins (shown once). */}
      <WelcomeTour />

      {/* Scheduled workers 30+ min late with no punch — one tap to backfill. */}
      <MissedClockInsCard token={token} />

      {/* Service-rate goals: utilization + effective rate vs target (admins). */}
      <RateGoalCard token={token} />

      {/* Stale paused jobs + seasonal-hold revive list (admins/supervisors). */}
      <JobsHousekeepingCard token={token} />

      {/* Supervisor quick action: clock my team in/out (renders only if user supervises a team) */}
      <TeamQuickClock onPunch={() => { fetchDashboard(); fetchLiveStatus(); }} />

      {/* Personal time clock — admins & supervisors are on payroll too.
          Same widget as the top bar, but labelled so it can't be missed. */}
      <div
        className="ht-card px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        data-testid="dashboard-my-clock-card"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(30,136,229,0.12)' }}
          >
            <Clock className="w-5 h-5" style={{ color: '#1E88E5' }} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">Your time clock</div>
            <div className="text-xs truncate" style={{ color: '#A0AEC0' }}>
              Clock yourself in for payroll — your hours flow to the Payroll Workbench
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ClockStatusWidget variant="compact" />
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-lg transition-colors"
            style={{ color: '#1E88E5', background: 'rgba(30,136,229,0.1)' }}
            data-testid="dashboard-my-day-link"
          >
            My Day <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ===== ROW 1: Active Jobs Chart + Staff Clocked In ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Active Jobs Chart (3/5 width) */}
        <div className="lg:col-span-3 ht-card p-5" data-testid="active-jobs-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">
              Active Jobs <span style={{ color: '#A0AEC0' }}>({activeTotal})</span>
            </h3>
            <button
              onClick={() => navigate('/jobs')}
              className="text-xs px-2 py-1 rounded-lg transition-colors hover:bg-[rgba(30,136,229,0.2)]"
              style={{ color: '#1E88E5', background: 'rgba(30,136,229,0.1)' }}
              data-testid="active-jobs-view-all"
            >
              View all →
            </button>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <LegendDot color="#1E88E5" label="Status" />
            <LegendDot color="#00C896" label="Completed" />
            <LegendDot color="#64B5F6" label="Progress" />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={charts.active_jobs_chart || charts.hours_per_day || []}>
              <defs>
                <linearGradient id="gStatus" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E88E5" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#1E88E5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gCompleted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00C896" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00C896" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gProgress" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#64B5F6" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#64B5F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2D3F" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 12, color: '#fff' }} />
              <Area type="monotone" dataKey="pending" stroke="#1E88E5" strokeWidth={2} fill="url(#gStatus)" dot={false} />
              <Area type="monotone" dataKey="completed" stroke="#00C896" strokeWidth={2} fill="url(#gCompleted)" dot={false} />
              <Area type="monotone" dataKey="in_progress" stroke="#64B5F6" strokeWidth={2} fill="url(#gProgress)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Staff Clocked In (2/5 width) */}
        <div className="lg:col-span-2 ht-card p-5" data-testid="staff-clocked-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">
              Staff Clocked In <span style={{ color: '#A0AEC0' }}>({s.staff_clocked_in_count || 0}/{s.total_staff || 0})</span>
            </h3>
            <button
              onClick={() => navigate('/reports?preset=today')}
              className="text-xs px-2 py-1 rounded-lg transition-colors hover:bg-[rgba(30,136,229,0.2)]"
              style={{ color: '#1E88E5', background: 'rgba(30,136,229,0.1)' }}
              data-testid="staff-clocked-view-all"
            >
              View all →
            </button>
          </div>
          <div className="space-y-3 max-h-[220px] overflow-y-auto">
            {(data?.staff_clocked_in || []).length > 0 ? (
              data.staff_clocked_in.map((s, i) => (
                <button
                  key={s.id || s.user_id || `${s.name}-${i}`}
                  onClick={() => navigate(`/reports?user_id=${s.id || s.user_id || ''}&preset=today`)}
                  className="w-full flex items-center gap-3 text-left px-1 py-1 -mx-1 rounded-lg transition-colors hover:bg-white/5 group"
                  data-testid={`staff-clocked-row-${s.id || s.user_id || i}`}
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: ['#1E88E5', '#00C896', '#FFA000', '#A78BFA'][i % 4] }}>
                    {s.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{s.name}</p>
                    <p className="text-xs capitalize" style={{ color: '#64748B' }}>{s.role}</p>
                  </div>
                  <span className="ht-pill ht-pill-green text-xs">{s.hours_today} Clocked</span>
                  <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#64748B' }} />
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <Users className="w-8 h-8 mx-auto mb-2" style={{ color: '#2D3748' }} />
                <p className="text-sm" style={{ color: '#64748B' }}>No staff clocked in today</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== Live Job Status Tiles ===== */}
      {liveStatus && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="live-job-status">
          <StatusTile
            label="In Progress"
            count={liveStatus.in_progress}
            color="#FFA000"
            onClick={() => navigate('/jobs?status=in_progress')}
            testId="live-tile-in-progress"
          />
          <StatusTile
            label="Paused"
            count={liveStatus.paused}
            color="#A78BFA"
            onClick={() => navigate('/jobs?status=paused')}
            testId="live-tile-paused"
            subline={
              liveStatus.paused_reasons && liveStatus.paused_reasons.length > 0
                ? liveStatus.paused_reasons
                    .slice(0, 2)
                    .map((r) => `${r.reason} (${r.count})`)
                    .join(' · ')
                : null
            }
          />
          <StatusTile
            label="Extended"
            count={liveStatus.extended}
            color="#8B5CF6"
            onClick={() => navigate('/jobs?filter=extended')}
            testId="live-tile-extended"
          />
          <StatusTile
            label="Overdue"
            count={liveStatus.overdue}
            color="#EF4444"
            onClick={() => navigate('/jobs?filter=overdue')}
            testId="live-tile-overdue"
          />
        </div>
      )}

      {/* ===== ROW 1.5: Estimate Performance + Project Profitability + Revenue by Project =====
          Three KPI tiles, admin/supervisor-only (each internally 403s for
          workers). 3-up on xl+, 2-up on lg, stacked below. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-5">
        <EstimatePerformanceTile />
        <ProjectProfitabilityTile />
        <RevenueByProjectTile />
        <SatisfactionTile />
      </div>

      {/* ===== ROW 2: Pending Approvals + Recent Jobs ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Pending / Job Summary (2/5 width) */}
        <div className="lg:col-span-2 ht-card p-5" data-testid="job-summary">
          <h3 className="text-white font-semibold mb-4">
            Job Summary <span style={{ color: '#A0AEC0' }}>({activeTotal + (s.jobs_completed_week || 0)})</span>
          </h3>
          <div className="space-y-3">
            <SummaryRow icon="circle" color="#EF4444" label="Overdue" count={s.overdue_jobs || 0} href="/jobs?filter=overdue" />
            <SummaryRow icon="circle" color="#FFA000" label="In Progress" count={s.jobs_in_progress || 0} href="/jobs?status=in_progress" />
            <SummaryRow icon="circle" color="#1E88E5" label="Pending" count={s.jobs_pending || 0} href="/jobs?status=pending" />
            <SummaryRow icon="circle" color="#A78BFA" label="Paused" count={s.jobs_paused || 0} href="/jobs?status=paused" />
            <SummaryRow icon="circle" color="#00C896" label="Completed" count={s.jobs_completed_week || 0} href="/jobs?status=completed" />
          </div>
          
          {/* Quick Actions */}
          <div className="mt-5 pt-4 flex gap-2" style={{ borderTop: '1px solid #2D3748' }}>
            <button onClick={() => navigate('/jobs')} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white ht-btn-glow" style={{ background: '#1E88E5' }} data-testid="create-job-btn">
              <Plus className="w-4 h-4" /> New Job
            </button>
            <button onClick={() => navigate('/estimates/new')} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white" style={{ background: '#10B981' }} data-testid="dashboard-new-estimate-btn">
              <Plus className="w-4 h-4" /> New Estimate
            </button>
            <button onClick={() => navigate('/estimates')} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium" style={{ color: '#A0AEC0', border: '1px solid #2D3748' }} data-testid="dashboard-view-estimates-btn">
              <Eye className="w-4 h-4" /> Estimates
            </button>
          </div>
        </div>

        {/* Recent Jobs Table (3/5 width) */}
        <div className="lg:col-span-3 ht-card p-5" data-testid="recent-jobs-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Recent Jobs</h3>
            {isAdmin && (
              <button onClick={() => { if (exportStart && exportEnd) handleExportCSV(); }} className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1" style={{ color: '#1E88E5', background: 'rgba(30,136,229,0.1)' }}>
                <Download className="w-3 h-3" /> Export
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #2D3748' }}>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Job ID</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Client</th>
                  <th className="text-left py-2 px-2 font-medium hidden md:table-cell" style={{ color: '#64748B' }}>Service</th>
                  <th className="text-left py-2 px-2 font-medium hidden md:table-cell" style={{ color: '#64748B' }}>Tech</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_jobs || []).length > 0 ? (
                  data.recent_jobs.map((job, i) => (
                    <tr key={job.id || job.job_id || `${job.job_id || 'row'}-${i}`} style={{ borderBottom: '1px solid #1F2D3F' }}>
                      <td className="py-2.5 px-2" style={{ color: '#1E88E5' }}>{job.job_id}</td>
                      <td className="py-2.5 px-2 text-white">{job.client || '-'}</td>
                      <td className="py-2.5 px-2 hidden md:table-cell" style={{ color: '#A0AEC0' }}>{job.service || '-'}</td>
                      <td className="py-2.5 px-2 hidden md:table-cell text-white">{job.tech}</td>
                      <td className="py-2.5 px-2"><JobStatusPill status={job.status} /></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-8 text-center" style={{ color: '#64748B' }}>
                      No jobs yet. <button onClick={() => navigate('/jobs')} style={{ color: '#1E88E5' }}>Create your first job</button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Payroll Export Row */}
          {isAdmin && (
            <div className="mt-4 pt-3 flex items-center gap-2" style={{ borderTop: '1px solid #2D3748' }}>
              <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)}
                className="flex-1 px-2 py-1.5 rounded-lg text-xs text-white" style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="export-start-date" />
              <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)}
                className="flex-1 px-2 py-1.5 rounded-lg text-xs text-white" style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                data-testid="export-end-date" />
              <button onClick={handleExportCSV} disabled={!exportStart || !exportEnd || exporting}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-40 ht-btn-glow" style={{ background: '#1E88E5' }} data-testid="export-csv-btn">
                {exporting ? '...' : 'CSV'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ===== ROW 3: QuickBooks Payroll + Bulk Sync ===== */}
      {isAdmin && (
        <>
          <QBHealthBadge token={token} onClickErrors={() => navigate('/settings?section=quickbooks')} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <QBPayrollSection token={token} />
            <QBBulkSyncSection token={token} />
          </div>
        </>
      )}

      {/* ===== Active Extended Jobs ===== */}
      {extendedJobs.length > 0 && (
        <div className="ht-card p-5" data-testid="extended-jobs-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">
              Active Extended Jobs <span style={{ color: '#A0AEC0' }}>({extendedJobs.length})</span>
            </h3>
            <button
              onClick={() => navigate('/jobs')}
              className="text-xs px-2 py-1 rounded-lg transition-colors hover:bg-[rgba(30,136,229,0.2)]"
              style={{ color: '#1E88E5', background: 'rgba(30,136,229,0.1)' }}
              data-testid="extended-jobs-view-all"
            >
              View all →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #2D3748' }}>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Job</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Worker</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Status</th>
                  <th className="text-left py-2 px-2 font-medium hidden md:table-cell" style={{ color: '#64748B' }}>Sessions</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>Hours</th>
                  <th className="text-left py-2 px-2 font-medium hidden md:table-cell" style={{ color: '#64748B' }}>Days</th>
                  <th className="text-left py-2 px-2 font-medium hidden md:table-cell" style={{ color: '#64748B' }}>Last Activity</th>
                  <th className="text-right py-2 px-2 font-medium" style={{ color: '#64748B' }}></th>
                </tr>
              </thead>
              <tbody>
                {extendedJobs.map((j) => (
                  <tr
                    key={j.id}
                    onClick={() => navigate(`/jobs?job=${j.id}`)}
                    className="cursor-pointer transition-colors hover:bg-white/5 active:bg-white/10"
                    style={{ borderBottom: '1px solid #1F2D3F' }}
                    data-testid={`extended-row-${j.id}`}
                  >
                    <td className="py-2.5 px-2 text-white font-medium">
                      {j.title}
                      {j.customer_name && (
                        <div className="text-xs" style={{ color: '#64748B' }}>{j.customer_name}</div>
                      )}
                    </td>
                    <td className="py-2.5 px-2" style={{ color: '#A0AEC0' }}>{j.assigned_worker_name || '-'}</td>
                    <td className="py-2.5 px-2">
                      <JobStatusPill status={j.status} />
                      {j.status === 'paused' && j.paused_reason && (
                        <div className="text-xs mt-1" style={{ color: '#FFA000' }}>
                          {j.paused_reason}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-white hidden md:table-cell">{j.sessions_count}</td>
                    <td className="py-2.5 px-2 text-white">{j.total_hours_worked.toFixed(1)} h</td>
                    <td className="py-2.5 px-2 hidden md:table-cell" style={{ color: '#A0AEC0' }}>
                      {j.days_since_start != null ? `${j.days_since_start}d` : '-'}
                    </td>
                    <td className="py-2.5 px-2 hidden md:table-cell" style={{ color: '#A0AEC0' }}>
                      {j.last_activity_at ? new Date(j.last_activity_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setTimelineJob(j); }}
                        className="px-3 py-1 rounded-md text-xs font-medium"
                        style={{ background: 'rgba(139,92,246,0.15)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.3)' }}
                        data-testid={`view-timeline-${j.id}`}
                      >
                        View Timeline
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Timeline modal */}
      {timelineJob && (
        <TimelineModal job={timelineJob} onClose={() => setTimelineJob(null)} />
      )}
    </div>
  );
}

/* ===== Sub-components ===== */

function StatusTile({ label, count, color, onClick, subline, testId }) {
  return (
    <button
      onClick={onClick}
      className="ht-card p-4 text-left transition-transform hover:scale-[1.01] group"
      style={{ borderLeft: `4px solid ${color}` }}
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-wider mb-1" style={{ color: '#64748B' }}>
        {label}
      </div>
      <div className="text-3xl font-bold text-white">{count ?? 0}</div>
      {subline && (
        <div className="text-[11px] mt-2 truncate" style={{ color }} title={subline}>
          {subline}
        </div>
      )}
      <div
        className="text-[11px] mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: '#1E88E5' }}
      >
        View all →
      </div>
    </button>
  );
}

function TimelineModal({ job, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="extended-timeline-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">{job.title}</h3>
            {job.customer_name && <p className="text-sm" style={{ color: '#A0AEC0' }}>{job.customer_name}</p>}
          </div>
          <button onClick={onClose} className="text-white opacity-60 hover:opacity-100 px-2" aria-label="Close">✕</button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="ht-card p-3">
            <div className="text-xs" style={{ color: '#64748B' }}>Sessions</div>
            <div className="text-xl font-semibold text-white">{job.sessions_count}</div>
          </div>
          <div className="ht-card p-3">
            <div className="text-xs" style={{ color: '#64748B' }}>Total Hours</div>
            <div className="text-xl font-semibold text-white">{job.total_hours_worked.toFixed(1)}h</div>
          </div>
          <div className="ht-card p-3">
            <div className="text-xs" style={{ color: '#64748B' }}>Days Since Start</div>
            <div className="text-xl font-semibold text-white">{job.days_since_start ?? '-'}</div>
          </div>
        </div>
        <h4 className="text-sm font-semibold text-white mb-3">Work Sessions</h4>
        <div className="space-y-2">
          {(job.work_sessions || []).map((s, i) => {
            const started = s.started_at ? new Date(s.started_at) : null;
            const ended = s.ended_at ? new Date(s.ended_at) : null;
            const hours = s.hours_worked ?? 0;
            return (
              <div
                key={s.id || i}
                className="p-3 rounded-lg flex items-center justify-between"
                style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              >
                <div>
                  <div className="text-sm text-white">
                    Session {i + 1} — {started ? started.toLocaleDateString() : '-'}
                  </div>
                  <div className="text-xs" style={{ color: '#A0AEC0' }}>
                    {started ? started.toLocaleTimeString() : '-'} {ended ? `to ${ended.toLocaleTimeString()}` : ' (in progress)'} · {s.worker_name || ''}
                  </div>
                </div>
                <div className="text-sm font-semibold text-white">
                  {hours ? `${hours.toFixed(1)}h` : 'active'}
                </div>
              </div>
            );
          })}
          {(!job.work_sessions || job.work_sessions.length === 0) && (
            <p className="text-sm text-center py-4" style={{ color: '#64748B' }}>No sessions recorded yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <span className="text-xs" style={{ color: '#A0AEC0' }}>{label}</span>
    </div>
  );
}

function SummaryRow({ color, label, count, href }) {
  const navigate = useNavigate();
  if (href) {
    const target = count > 0 ? href : `${href}&notice=empty&label=${encodeURIComponent(label)}`;
    return (
      <button
        onClick={() => navigate(target)}
        className="w-full flex items-center justify-between py-1.5 px-2 -mx-2 rounded-lg text-left transition-colors hover:bg-white/5 group"
        data-testid={`summary-row-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full" style={{ background: color }} />
          <span className="text-sm" style={{ color: '#A0AEC0' }}>{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-white">{count}</span>
          <ChevronRight
            className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 transition-opacity"
            style={{ color: '#64748B' }}
          />
        </div>
      </button>
    );
  }
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-3">
        <span className="w-3 h-3 rounded-full" style={{ background: color }} />
        <span className="text-sm" style={{ color: '#A0AEC0' }}>{label}</span>
      </div>
      <span className="text-sm font-semibold text-white">{count}</span>
    </div>
  );
}

function JobStatusPill({ status }) {
  const styles = {
    pending: { bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0' },
    todo: { bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0' },
    accepted: { bg: 'rgba(30,136,229,0.15)', color: '#1E88E5' },
    in_progress: { bg: 'rgba(30,136,229,0.15)', color: '#1E88E5' },
    paused: { bg: 'rgba(255,160,0,0.15)', color: '#FFA000' },
    completed: { bg: 'rgba(0,200,150,0.15)', color: '#00C896' },
    rejected: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: s.bg, color: s.color }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function DashSkeleton() {
  return (
    <div className="p-6 space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 ht-card p-5 animate-pulse"><div className="w-32 h-5 rounded mb-4" style={{ background: '#2D3748' }} /><div className="w-full h-[220px] rounded-xl" style={{ background: '#1F2D3F' }} /></div>
        <div className="lg:col-span-2 ht-card p-5 animate-pulse"><div className="w-32 h-5 rounded mb-4" style={{ background: '#2D3748' }} />{[...Array(4)].map((_, i) => <div key={i} className="flex items-center gap-3 py-2"><div className="w-9 h-9 rounded-full" style={{ background: '#2D3748' }} /><div className="flex-1 h-4 rounded" style={{ background: '#2D3748' }} /></div>)}</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 ht-card p-5 animate-pulse"><div className="w-32 h-5 rounded mb-4" style={{ background: '#2D3748' }} />{[...Array(5)].map((_, i) => <div key={i} className="h-6 rounded mb-2" style={{ background: '#2D3748' }} />)}</div>
        <div className="lg:col-span-3 ht-card p-5 animate-pulse"><div className="w-32 h-5 rounded mb-4" style={{ background: '#2D3748' }} />{[...Array(4)].map((_, i) => <div key={i} className="h-8 rounded mb-2" style={{ background: '#2D3748' }} />)}</div>
      </div>
    </div>
  );
}


function QBPayrollSection({ token }) {
  const [payStart, setPayStart] = useState('');
  const [payEnd, setPayEnd] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const API = `${window.__BACKEND_URL__}/api`;

  const handleSync = async () => {
    if (!payStart || !payEnd) return;
    setSyncing(true);
    setResult(null);
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      let acknowledgeOverlap = false;
      let started = null;
      while (!started) {
        try {
          started = (await axios.post(`${API}/qb/sync-timesheets`, { start_date: payStart, end_date: payEnd, acknowledge_overlap: acknowledgeOverlap, async: true }, auth)).data;
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.code === 'overlap_warning') {
            if (!window.confirm(`${detail.message}\n\nContinue with the push?`)) return;
            acknowledgeOverlap = true;
          } else if (err.response?.status === 409 && detail?.code === 'push_in_progress') {
            started = { push_id: detail.push_id };
          } else {
            throw err;
          }
        }
      }
      let data = started;
      if (started.push_id) {
        for (;;) {
          await new Promise((r) => setTimeout(r, 2000));
          const st = (await axios.get(`${API}/qb/sync-timesheets/status/${started.push_id}`, auth)).data;
          if (st.status === 'running') continue;
          data = st.status === 'failed' ? { success: false, error: st.error || 'Sync failed' } : st.result;
          break;
        }
      }
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResult({ success: false, error: typeof detail === 'string' ? detail : 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="ht-card p-5" data-testid="qb-payroll">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4" style={{ color: '#1E88E5' }} /> QuickBooks Payroll
      </h3>
      <p className="text-xs mb-3" style={{ color: '#64748B' }}>Push timesheets to QuickBooks for payroll processing.</p>
      <div className="flex items-center gap-2 mb-3">
        <input type="date" value={payStart} onChange={e => setPayStart(e.target.value)}
          className="flex-1 px-2 py-1.5 rounded-lg text-xs text-white" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="qb-pay-start" />
        <span style={{ color: '#64748B' }}>to</span>
        <input type="date" value={payEnd} onChange={e => setPayEnd(e.target.value)}
          className="flex-1 px-2 py-1.5 rounded-lg text-xs text-white" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="qb-pay-end" />
      </div>
      <button onClick={handleSync} disabled={!payStart || !payEnd || syncing}
        className="w-full py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-40 ht-btn-glow" style={{ background: '#1E88E5' }} data-testid="qb-payroll-sync">
        {syncing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Run Payroll Export to QuickBooks'}
      </button>
      {result && (
        <div className="mt-3 p-3 rounded-xl text-xs" style={{ background: result.success ? 'rgba(0,200,150,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${result.success ? 'rgba(0,200,150,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
          {result.success ? (
            <p style={{ color: '#00C896' }}>Pushed {result.entries_pushed} entries for {result.workers_synced} workers{result.errors > 0 ? ` (${result.errors} errors)` : ''}</p>
          ) : (
            <p style={{ color: '#EF4444' }}>{result.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

function QBBulkSyncSection({ token }) {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const API = `${window.__BACKEND_URL__}/api`;

  const handleBulkSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/qb/sync-jobs-bulk`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="ht-card p-5" data-testid="qb-bulk-sync">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <Briefcase className="w-4 h-4" style={{ color: '#00C896' }} /> QuickBooks Invoicing
      </h3>
      <p className="text-xs mb-3" style={{ color: '#64748B' }}>Send all completed jobs this week to QuickBooks as invoices.</p>
      <button onClick={handleBulkSync} disabled={syncing}
        className="w-full py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-40 ht-btn-glow" style={{ background: '#2CA01C' }} data-testid="qb-bulk-sync-btn">
        {syncing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Send All Completed Jobs to QuickBooks'}
      </button>
      {result && (
        <div className="mt-3 p-3 rounded-xl text-xs" style={{ background: result.synced > 0 ? 'rgba(0,200,150,0.1)' : result.error ? 'rgba(239,68,68,0.1)' : 'rgba(160,174,192,0.1)', border: '1px solid #2D3748' }}>
          {result.error ? (
            <p style={{ color: '#EF4444' }}>{result.error}</p>
          ) : (
            <p style={{ color: '#00C896' }}>{result.message}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * QuickBooks Online sync-health badge.
 * Pulls GET /api/qb/health every 60s and renders:
 *   - green pill   → synced in last 24h, no errors
 *   - amber pill   → stale (>24h or never) OR disconnected
 *   - red pill     → unresolved sync errors
 */
function QBHealthBadge({ token, onClickErrors }) {
  const [health, setHealth] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const API_BASE = `${window.__BACKEND_URL__}/api`;

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const r = await axios.get(`${API_BASE}/qb/health`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setHealth(r.data);
      } catch {
        if (!cancelled) setHealth({ status: 'disconnected', connected: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHealth();
    const t = setInterval(fetchHealth, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token, API_BASE]);

  if (loading || !health) return null;

  const status = health.status; // healthy | stale | warn | disconnected
  const styleMap = {
    healthy:      { fg: '#00C896', bg: 'rgba(0,200,150,0.12)',  border: 'rgba(0,200,150,0.35)', dot: '#00C896', label: 'Healthy' },
    stale:        { fg: '#FFA000', bg: 'rgba(255,160,0,0.12)',  border: 'rgba(255,160,0,0.35)', dot: '#FFA000', label: 'Stale'   },
    warn:         { fg: '#EF4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.35)', dot: '#EF4444', label: 'Errors'  },
    disconnected: { fg: '#64748B', bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.35)', dot: '#64748B', label: 'Disconnected' },
  };
  const s = styleMap[status] || styleMap.disconnected;

  const ageLabel = (() => {
    if (!health.last_sync_at) return 'never synced';
    const ts = new Date(health.last_sync_at);
    const mins = Math.max(0, Math.floor((Date.now() - ts.getTime()) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  })();

  return (
    <div
      className="ht-card px-4 py-3 flex items-center justify-between"
      data-testid="qb-health-badge"
      style={{ border: `1px solid ${s.border}`, background: s.bg }}
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* QBO green dot icon */}
        <div className="w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center" style={{ background: 'rgba(44,160,28,0.15)' }}>
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#2CA01C">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6z"/>
          </svg>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-white truncate" data-testid="qb-health-title">
              QuickBooks Online
            </p>
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
              style={{ color: s.fg, background: 'rgba(0,0,0,0.25)', border: `1px solid ${s.border}` }}
              data-testid={`qb-health-pill-${status}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
              {s.label}
            </span>
          </div>
          <p className="text-xs mt-0.5 truncate" style={{ color: '#A0AEC0' }}>
            {health.connected
              ? (
                <>
                  {health.company_name ? <strong className="text-white">{health.company_name}</strong> : <em>Connected</em>}
                  <span> · Last sync {ageLabel}{health.last_sync_type ? ` (${health.last_sync_type})` : ''}</span>
                  {health.unresolved_errors > 0 && (
                    <span style={{ color: '#EF4444' }}> · {health.unresolved_errors} unresolved error{health.unresolved_errors !== 1 ? 's' : ''}</span>
                  )}
                </>
              )
              : 'Not connected. Configure in Settings → QuickBooks Online.'
            }
          </p>
        </div>
      </div>
      {health.unresolved_errors > 0 && typeof onClickErrors === 'function' && (
        <button
          onClick={onClickErrors}
          className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg font-medium"
          style={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.05)' }}
          data-testid="qb-health-view-errors"
        >
          View errors
        </button>
      )}
    </div>
  );
}
